import { useState } from 'react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    user_email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitStatus('idle');
    setErrorMessage('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage('담당자명을 입력해 주세요');
      return false;
    }
    if (!formData.phone.trim()) {
      setErrorMessage('휴대폰 번호를 입력해 주세요');
      return false;
    }
    if (!formData.user_email.trim()) {
      setErrorMessage('이메일 주소를 입력해 주세요');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.user_email)) {
      setErrorMessage('올바른 이메일 주소를 입력해 주세요');
      return false;
    }
    if (!formData.message.trim()) {
      setErrorMessage('상담 내용을 입력해 주세요');
      return false;
    }
    if (formData.message.length > 1000) {
      setErrorMessage('상담 내용은 1000자 이내로 입력해 주세요');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name.trim());
      formBody.append('phone', formData.phone.trim());
      formBody.append('user_email', formData.user_email.trim());
      formBody.append('message', formData.message.trim());

      const response = await fetch('https://readdy.ai/api/form/d6fun2gr225iqdhs0gpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', user_email: '', message: '' });
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setSubmitStatus('error');
        setErrorMessage('전송 실패. 다시 시도해 주세요');
      }
    } catch (error) {
      console.error('송신 오류:', error);
      setSubmitStatus('error');
      setErrorMessage('네트워크 오류. 인터넷 연결을 확인해 주세요');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7FAFF] to-white">
      {/* 헤더 */}
      <header className="bg-white/95 backdrop-blur-lg shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 lg:gap-4 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/b1989ece91adf9f4c331600c20412c5d/d4a602daafc972804a8f79cf255edce9.png"
              alt="K-NEXUS"
              className="h-9 lg:h-12 w-auto object-contain"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-base lg:text-xl tracking-wide text-[#1A1A2E]">K-NEXUS</span>
              <span className="text-[#3B9AFF] font-semibold text-[10px] lg:text-xs tracking-wider">MARKETING</span>
            </div>
          </a>
          <a
            href="/"
            className="text-[#1A1A2E] hover:text-[#3B9AFF] transition-colors duration-300 cursor-pointer text-sm lg:text-base font-medium flex items-center gap-2"
          >
            <i className="ri-home-line text-base lg:text-lg"></i>
            이전으로
          </a>
        </div>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="max-w-2xl lg:max-w-3xl mx-auto px-5 lg:px-8 py-12 lg:py-24">
        <div className="text-center mb-10 lg:mb-16">
          <p className="text-[#3B9AFF] text-xs lg:text-sm font-bold tracking-[3px] mb-3 lg:mb-4 uppercase">CONTACT</p>
          <h1 className="text-3xl lg:text-5xl font-bold text-[#1A1A2E] mb-4 lg:mb-6">상담 신청</h1>
          <div className="text-sm lg:text-base text-[#555] leading-relaxed text-left bg-[#F7FAFF] border border-[#E0EDFF] rounded-xl p-6 lg:p-8 space-y-4">
            <p className="font-bold text-[#1A1A2E] text-base lg:text-lg">담당자님께</p>
            <p>
              사내 사정으로 인해<br />
              「외부 업체에 임의로 문의해도 되는지에 대한 불안」<br />
              「대표에게 보고가 필요해 번거로운 점」<br />
              「아직 검토 단계인데 공식 문의 형식이 되는 것이 부담스러운 점」<br />
              「회사명을 밝힘으로써 책임이 발생하는 것에 대한 우려」<br />
              등의 이유로 문의를 망설이고 계신 담당자분들도 계실 것으로 생각합니다.
            </p>
            <p>
              이와 같은 경우에도 당사에서는 회사명 입력 없이 상담이 가능합니다.<br />
              우선 성함만 입력해 주셔도 괜찮습니다.
            </p>
            <p>
              이후 사내 검토를 거쳐 정식 문의로 다시 연락 주시는 흐름도 문제 없습니다.
            </p>
            <p>
              모처럼 광고를 보시고 본 페이지까지 방문해 주신 이 기회에,<br />
              우선 성함만이라도 아래 양식에 남겨 주시면 감사하겠습니다.
            </p>
          </div>
        </div>

        {/* 폼 */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-[#E0EDFF] p-7 lg:p-14">
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6 lg:space-y-8">

            {/* 이름 */}
            <div>
              <label htmlFor="name" className="block text-sm lg:text-base font-bold text-[#1A1A2E] mb-2 lg:mb-3">
                담당자명 <span className="text-[#FF6B6B]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 lg:px-5 py-3 lg:py-4 border-2 border-[#E0EDFF] rounded-lg text-sm lg:text-base focus:border-[#3B9AFF] focus:outline-none transition-colors"
                placeholder="홍길동"
              />
            </div>

            {/* 휴대폰 번호 */}
            <div>
              <label htmlFor="phone" className="block text-sm lg:text-base font-bold text-[#1A1A2E] mb-2 lg:mb-3">
                휴대폰 번호 <span className="text-[#FF6B6B]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 lg:px-5 py-3 lg:py-4 border-2 border-[#E0EDFF] rounded-lg text-sm lg:text-base focus:border-[#3B9AFF] focus:outline-none transition-colors"
                placeholder="010-1234-5678"
              />
            </div>

            {/* 이메일 주소 */}
            <div>
              <label htmlFor="user_email" className="block text-sm lg:text-base font-bold text-[#1A1A2E] mb-2 lg:mb-3">
                이메일 주소 <span className="text-[#FF6B6B]">*</span>
              </label>
              <input
                type="email"
                id="user_email"
                name="user_email"
                value={formData.user_email}
                onChange={handleChange}
                required
                className="w-full px-4 lg:px-5 py-3 lg:py-4 border-2 border-[#E0EDFF] rounded-lg text-sm lg:text-base focus:border-[#3B9AFF] focus:outline-none transition-colors"
                placeholder="example@company.com"
              />
            </div>

            {/* 상담 내용 */}
            <div>
              <label htmlFor="message" className="block text-sm lg:text-base font-bold text-[#1A1A2E] mb-2 lg:mb-3">
                상담 내용 <span className="text-[#FF6B6B]">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={1000}
                rows={6}
                className="w-full px-4 lg:px-5 py-3 lg:py-4 border-2 border-[#E0EDFF] rounded-lg text-sm lg:text-base focus:border-[#3B9AFF] focus:outline-none transition-colors resize-none"
                placeholder="불릿 포인트 형식 등으로 작성하셔도 괜찮으니, 문의 내용을 간단히 1,000자 이내로 작성해 주시기 바랍니다.
또한 회사명 또는 병원명을 알려주실 수 있는 경우에는 여기에 기재해 주시기 바랍니다."
              ></textarea>
              <p className="text-xs lg:text-sm text-[#999] mt-2">
                {formData.message.length} / 1000자
              </p>
            </div>

            {/* 전송 상태 */}
            {submitStatus === 'success' && (
              <div className="bg-[#E8F5E9] border-2 border-[#4CAF50] rounded-lg p-4 flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-lg text-[#4CAF50]"></i>
                </div>
                <p className="text-sm font-semibold text-[#2E7D32]">전송 완료! 곧 연락드리겠습니다.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-[#FFEBEE] border-2 border-[#F44336] rounded-lg p-4 flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-fill text-lg text-[#F44336]"></i>
                </div>
                <p className="text-sm font-semibold text-[#C62828]">{errorMessage || '전송 실패. 다시 시도해 주세요'}</p>
              </div>
            )}

            {/* 전송 버튼 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#3B9AFF] text-white px-6 py-4 lg:py-5 rounded-lg text-sm lg:text-base font-bold hover:bg-[#2B8AEF] hover:shadow-lg hover:shadow-[#3B9AFF]/30 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line text-lg animate-spin"></i>
                  전송 중...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill text-lg"></i>
                  전송하기
                </>
              )}
            </button>
          </form>
        </div>

        {/* 직접 연락처 */}
        <div className="mt-10 lg:mt-16 text-center">
          <p className="text-sm lg:text-base text-[#777] mb-4 lg:mb-6">또는 직접 이메일, 인스타그램으로 문의해 주세요</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 lg:gap-6">
            <a
              href="mailto:knmkt.111@knexus1.com"
              className="inline-flex items-center gap-2 text-[#3B9AFF] hover:text-[#2B8AEF] font-bold text-sm lg:text-base transition-colors cursor-pointer"
            >
              <i className="ri-mail-line text-lg lg:text-xl"></i>
              knmkt.111@knexus1.com
            </a>
            <a
              href="https://www.instagram.com/ktjpkr.1/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#E4405F] hover:text-[#D62976] font-bold text-sm lg:text-base transition-colors cursor-pointer"
            >
              <i className="ri-instagram-line text-lg lg:text-xl"></i>
              @ktjpkr.1
            </a>
          </div>
        </div>
      </main>

      {/* 푸터 */}
      <footer className="bg-[#F0F5FF] border-t border-[#E0EDFF] py-8 lg:py-12 px-5 lg:px-10 mt-16 lg:mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-6">
            <div className="flex items-center gap-3 lg:gap-4">
              <img src="https://static.readdy.ai/image/b1989ece91adf9f4c331600c20412c5d/d4a602daafc972804a8f79cf255edce9.png" alt="K-NEXUS" className="h-9 lg:h-12 w-auto object-contain" />
              <div className="flex flex-col leading-tight">
                <span className="text-[#1A1A2E] font-bold text-base lg:text-lg tracking-wide">K-NEXUS</span>
                <span className="text-[#3B9AFF] font-semibold text-[10px] lg:text-xs tracking-wider">MARKETING</span>
              </div>
            </div>
            <div className="text-[#999] text-xs flex flex-col lg:flex-row gap-2 lg:gap-6 items-center">
              <p>&copy; 2026 K-NEXUS. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
